/* ---------------------------------------------------------------------------
   PK Gallery Wall Builder

   HOW PRINTS ARE SHOWN: every framed product image is 1100x1354 with the
   frame on a white background. The frame sits in the same place for every
   colour of the same product, so the builder finds the frame box ONCE per
   product from its Black image (the dark frame makes the edges exact) and
   crops every colour to that box. If the image cannot be read (cross-origin)
   or does not look like a frame image, a box for that shape is used instead
   (see FALLBACK_BOXES).

   UNFRAMED prints use the product's unframed mockup ("..._NF.jpg", usually
   the 2nd product image): the print centred on a grey wall with window light
   and a hard shadow strip under it. Measured on 15 products, Sept 2026:
     - left/right/top edges are found as straight sharp edges running along
       most of the side (window light is soft and diagonal, so it never
       qualifies);
     - every template puts the print at the same x (284..912 of 1200), so a
       side that disagrees (white paper against bright window light) is
       corrected from the other side or the template;
     - the BOTTOM is never measured: the shadow strip under the print is
       sharper than the paper edge. The print is centred, so bottom = mirror
       of top.
   If there is no NF image or it cannot be read, it falls back to cropping
   inside the frame of the Black image.

   QUALITY: each print is drawn onto its own <canvas> from the full-size image
   at the screen's pixel density, shrinking in halving steps (one big CSS
   downscale looks soft and jagged).

   SIZES ARE TRUE TO SCALE: each room says how wide the whole photo is at the
   wall in cm. A print's inches come from its Size option ("16.5x23.4" (A2)"),
   flipped for products tagged "landscape".

   CART: "Add wall to basket" adds every print in one /cart/add.js call, then
   calls window.pkCart.refresh() (pk-cart.js) to reload and open the drawer.

   OFFER BAR mirrors the cart (snippets/pk-cart-offer.liquid, pk-cart.js):
     1. free shipping at N paid prints (theme setting, default 2)
     2. at 3 paid prints, one free print (A3/12x16/A4/6x8)
     3. once the free print is claimed and £150 is spent, one free Large print
        (A2/18x24)
   Prints already in the basket count. A claimed print is chosen from the
   "free-gift" collection, hangs on the wall and is added with the same
   hidden line property the cart uses (_pk_gift = free-A3, large-A2, ...),
   so the discount app prices it. The app takes a FIXED amount off per size
   (see GIFT_OFF), and the wall charges the same. Spend = real
   money on every line except the Large print's own, as in the cart. A gift
   whose condition stops being met goes back to full price and is added
   untagged.
--------------------------------------------------------------------------- */
(function () {
  'use strict';

  var FRAME_SWATCHES = { black: '#111111', white: '#f4f4f4', oak: '#d2a76a', walnut: '#6b3f26', natural: '#d2a76a', unframed: '#ffffff' };
  var UNFRAMED = ['unframed', 'no frame', 'none', ''];

  /* Frame boxes measured on the live 1100x1354 images, Sept 2026. */
  var IMG_W = 1100, IMG_H = 1354;
  var FALLBACK_BOXES = {
    'portrait-a':   [112, 66, 988, 1288],
    'portrait-34':  [112, 101, 988, 1252],
    'square':       [111, 239, 989, 1116],
    'landscape-a':  [112, 364, 988, 992],
    'landscape-34': [112, 344, 988, 1011]
  };

  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function money(cents) {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: code }).format((cents || 0) / 100);
    } catch (e) { return ((cents || 0) / 100).toFixed(2); }
  }

  function isUnframed(v) { return UNFRAMED.indexOf(String(v || '').toLowerCase().trim()) !== -1; }

  function parseInches(size) {
    var m = String(size || '').match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    if (!m) return null;
    return { w: parseFloat(m[1]), h: parseFloat(m[2]) };
  }

  function sameOrigin(url) {
    /* Serve CDN images from the shop's own domain so the canvas can read them. */
    if (!url) return url;
    if (url.indexOf('//') === 0) url = 'https:' + url;
    var m = url.match(/cdn\.shopify\.com\/s\/files\/[^/]+\/[^/]+\/[^/]+\/[^/]+\/files\/(.+)$/);
    if (m) return '/cdn/shop/files/' + m[1];
    return url;
  }

  function withWidth(url, w) {
    if (!url) return url;
    var clean = url.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = reject;
      img.src = src;
    });
  }

  /* Finds the frame's outer box (dark pixels along the middle row/column)
     and the frame's inner edge (first light pixel inside it). */
  function detectBox(img) {
    var W = img.naturalWidth, H = img.naturalHeight;
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    var row = ctx.getImageData(0, Math.round(H / 2), W, 1).data;
    var col = ctx.getImageData(Math.round(W / 2), 0, 1, H).data;
    function lum(d, i) { return (d[i * 4] + d[i * 4 + 1] + d[i * 4 + 2]) / 3; }
    var l = -1, r = -1, t = -1, b = -1, k;
    for (k = 0; k < W; k++) if (lum(row, k) < 80) { l = k; break; }
    for (k = W - 1; k >= 0; k--) if (lum(row, k) < 80) { r = k; break; }
    for (k = 0; k < H; k++) if (lum(col, k) < 80) { t = k; break; }
    for (k = H - 1; k >= 0; k--) if (lum(col, k) < 80) { b = k; break; }
    if (l < 0 || t < 0 || r - l < W * 0.4 || b - t < H * 0.25) return null;
    var f = 0;
    for (k = l; k < l + W * 0.1; k++) { if (lum(row, k) > 170) { f = k - l; break; } }
    if (!f) f = Math.round((r - l) * 0.03);
    return { box: [l, t, r + 1, b + 1], frame: f, w: W, h: H };
  }

  /* Paper edges of the print in an unframed (NF) mockup. Returns the box
     as fractions of the image so any image width can be used to draw it.
     See the header for why the bottom edge is mirrored from the top. */
  function detectNF(img) {
    var W = img.naturalWidth, H = img.naturalHeight;
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    var d = ctx.getImageData(0, 0, W, H).data;
    var L = new Float32Array(W * H), i, x, y;
    for (i = 0; i < W * H; i++) L[i] = d[i * 4] * 0.299 + d[i * 4 + 1] * 0.587 + d[i * 4 + 2] * 0.114;
    var TH = 10, NEED = 0.5;

    /* share of rows (middle 20%) with a sharp horizontal change at each x */
    var colFrac = new Float32Array(W), rows = 0;
    for (y = Math.round(H * 0.4); y < Math.round(H * 0.6); y += 2) {
      rows++;
      var o = y * W;
      for (x = 2; x < W - 2; x++) if (Math.abs(L[o + x + 2] - L[o + x - 2]) > TH) colFrac[x]++;
    }
    /* share of columns (middle 20%) with a sharp vertical change at each y */
    var rowFrac = new Float32Array(H), cols = 0;
    for (x = Math.round(W * 0.4); x < Math.round(W * 0.6); x += 2) {
      cols++;
      for (y = 2; y < H - 2; y++) if (Math.abs(L[(y + 2) * W + x] - L[(y - 2) * W + x]) > TH) rowFrac[y]++;
    }
    for (i = 0; i < W; i++) colFrac[i] /= rows;
    for (i = 0; i < H; i++) rowFrac[i] /= cols;

    function firstFrom(a, n, dir) {
      var k = dir > 0 ? 2 : n - 3;
      for (; dir > 0 ? k < n / 2 : k > n / 2; k += dir) {
        if (a[k] >= NEED) {
          while (a[k + dir] >= a[k]) k += dir;   /* settle on the peak */
          return k;
        }
      }
      return -1;
    }
    var l = firstFrom(colFrac, W, 1), r = firstFrom(colFrac, W, -1), t = firstFrom(rowFrac, H, 1);
    if (l < 0 || r < 0 || t < 0) return null;

    var k = W / 1200, ins = Math.max(2, Math.round(4 * k));
    l += ins; r -= ins;
    var TL = 284 * k, TR = 912 * k, tol = 6 * k;
    if (Math.abs(l - TL) > tol || Math.abs(r - TR) > tol) {
      if (Math.abs(l - TL) <= tol) r = Math.round(W - l);
      else if (Math.abs(r - TR) <= tol) l = Math.round(W - r);
      else { l = Math.round(TL); r = Math.round(TR); }
    }
    var b = Math.round(H - t) - ins;
    t += ins;
    if (r - l < W * 0.15 || b - t < H * 0.15) return null;
    return { box: [l / W, t / H, r / W, b / H], aspect: ((b - t) / H) / ((r - l) / W) * (H / W) };
  }

  /* Full-size images, loaded once each. */
  var imageCache = {};
  function getImage(src) {
    if (!imageCache[src]) imageCache[src] = loadImage(src);
    return imageCache[src];
  }

  /* Draws the crop box (fractions) of img onto canvas at w x h device pixels,
     halving in steps so a big downscale stays sharp. */
  function paintCrop(canvas, img, box, w, h) {
    var sx = box[0] * img.naturalWidth, sy = box[1] * img.naturalHeight;
    var sw = (box[2] - box[0]) * img.naturalWidth, sh = (box[3] - box[1]) * img.naturalHeight;
    var src = img, cw = sw, ch = sh, first = true;
    while (cw / 2 > w && ch / 2 > h) {
      var step = document.createElement('canvas');
      step.width = Math.round(cw / 2); step.height = Math.round(ch / 2);
      var sc = step.getContext('2d');
      sc.imageSmoothingEnabled = true; sc.imageSmoothingQuality = 'high';
      if (first) sc.drawImage(src, sx, sy, sw, sh, 0, 0, step.width, step.height);
      else sc.drawImage(src, 0, 0, cw, ch, 0, 0, step.width, step.height);
      src = step; cw = step.width; ch = step.height; first = false;
    }
    canvas.width = w; canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    if (first) ctx.drawImage(src, sx, sy, sw, sh, 0, 0, w, h);
    else ctx.drawImage(src, 0, 0, cw, ch, 0, 0, w, h);
  }

  /* Same codes pk-cart.js uses in _pk_gift. */
  function giftSizeCode(size) {
    var s = String(size || '').toLowerCase();
    if (s.indexOf('a4') !== -1) return 'A4';
    if (s.indexOf('a3') !== -1) return 'A3';
    if (s.indexOf('a2') !== -1) return 'A2';
    if (s.indexOf('6x8') !== -1) return '6x8';
    if (s.indexOf('12x16') !== -1) return '12x16';
    if (s.indexOf('18x24') !== -1) return '18x24';
    return '';
  }

  function Builder(root) {
    this.root = root;
    this.catalog = root.getAttribute('data-catalog');
    this.best = root.getAttribute('data-best');
    this.defaultSizes = (root.getAttribute('data-default-sizes') || 'A2, 18x24, 20x20').split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
    this.defaultFrame = (root.getAttribute('data-default-frame') || 'Black').trim().toLowerCase();
    this.frameMarginIn = (parseFloat(root.getAttribute('data-frame-margin')) || 8) / 10;
    this.rootUrl = root.getAttribute('data-root') || '/';
    this.cartUrl = root.getAttribute('data-cart-url') || '/cart';
    var split = function (a) { return (root.getAttribute(a) || '').split('|').map(function (x) { return x.trim(); }).filter(Boolean); };
    this.offer = {
      shipMode: root.getAttribute('data-ship-mode') || 'prints',
      shipMin: parseInt(root.getAttribute('data-ship-min'), 10) || 2,
      shipAmountCents: Math.round((parseFloat(root.getAttribute('data-ship-amount')) || 0) * 100),
      giftEvery: 3,
      bonusMinCents: Math.round((parseFloat(root.getAttribute('data-bonus-min')) || 150) * 100),
      giftCollection: root.getAttribute('data-gift-collection') || 'free-gift',
      sizes: { free: split('data-free-sizes'), large: split('data-large-sizes') }
    };
    this.basket = { paid: 0, spend: 0, freeLive: false, largeLive: false, prints: 0 };
    this.giftList = [];
    this.giftMode = null;

    this.el = {
      stage: $(root, '[data-pk-gw-stage]'),
      canvas: $(root, '[data-pk-gw-canvas]'),
      room: $(root, '[data-pk-gw-room]'),
      fallback: $(root, '[data-pk-gw-fallback]'),
      items: $(root, '[data-pk-gw-items]'),
      empty: $(root, '[data-pk-gw-empty]'),
      rooms: $(root, '[data-pk-gw-rooms]'),
      grid: $(root, '[data-pk-gw-grid]'),
      status: $(root, '[data-pk-gw-status]'),
      listTitle: $(root, '[data-pk-gw-list-title]'),
      search: $(root, '[data-pk-gw-search]'),
      filtersToggle: $(root, '[data-pk-gw-filters-toggle]'),
      filters: $(root, '[data-pk-gw-filters]'),
      panelTop: $(root, '.pk-gw__panel-top'),
      bestChip: $(root, '[data-pk-gw-best]'),
      filterCount: $(root, '[data-pk-gw-filter-count]'),
      scale: $(root, '[data-pk-gw-scale]'),
      subtotal: $(root, '[data-pk-gw-subtotal]'),
      compare: $(root, '[data-pk-gw-compare]'),
      add: $(root, '[data-pk-gw-add]'),
      close: $(root, '[data-pk-gw-close]'),
      pop: $(root, '[data-pk-gw-pop]'),
      popTitle: $(root, '[data-pk-gw-pop-title]'),
      popSize: $(root, '[data-pk-gw-pop-size]'),
      popFrames: $(root, '[data-pk-gw-pop-frames]'),
      popPrice: $(root, '[data-pk-gw-pop-price]'),
      popRemove: $(root, '[data-pk-gw-pop-remove]'),
      toast: $(root, '[data-pk-gw-toast]'),
      road: $(root, '[data-pk-gw-road]'),
      roadMsg: $(root, '[data-pk-gw-road-msg]'),
      roadNote: $(root, '[data-pk-gw-road-note]'),
      roadClaim: $(root, '[data-pk-gw-road-claim]'),
      giftbar: $(root, '[data-pk-gw-giftbar]'),
      giftbarText: $(root, '[data-pk-gw-giftbar-text]'),
      giftbarCancel: $(root, '[data-pk-gw-giftbar-cancel]'),
      panel: $(root, '.pk-gw__panel')
    };

    this.addLabel = this.el.add.textContent;

    var roomsJson = $(root, '[data-pk-gw-rooms-json]');
    try { this.rooms = JSON.parse(roomsJson.textContent) || []; } catch (e) { this.rooms = []; }
    if (!this.rooms.length) this.rooms = [{ src: '', thumb: '', w: 2000, h: 1333, wallCm: 420, hangY: 34, centerX: 50, label: 'Wall' }];
    this.roomIndex = 0;

    this.index = [];          /* catalogue (lightweight) */
    this.bestList = [];
    this.indexLoaded = false;
    this.products = {};       /* handle -> product.js */
    this.boxes = {};          /* handle -> detected frame box */
    this.nf = {};             /* handle -> unframed mockup crop (or false) */
    this.items = [];          /* prints on the wall */
    this.nextId = 1;
    this.selectedId = null;
    this.scale = 1;
    this.activeTags = {};     /* tag -> group ('style', 'mood', ...) */
    this.bestOn = true;       /* the Best sellers chip in Style */

    this.init();
  }

  Builder.prototype.init = function () {
    var self = this;
    this.setOffset();
    this.renderRoomButtons();
    this.setRoom(0);
    this.bind();
    this.loadBest().then(function () { self.renderList(); });
    this.loadCatalog().then(function () { self.renderList(); });
    this.fetchCollection(this.offer.giftCollection).then(function (list) {
      self.giftList = list;
      if (self.giftMode) self.renderList();
    }).catch(function () {});
    this.loadBasket();
    this.updateTotals();
    window.addEventListener('resize', function () { self.setOffset(); self.fitCanvas(); self.positionPop(); });
    /* the basket can change in another tab or in the drawer */
    window.addEventListener('focus', function () { self.loadBasket(); });
    document.addEventListener('cart:updated', function () { self.loadBasket(); });
  };

  /* Makes the builder fill the screen below the site header. The header
     (SS Header 13) is position:fixed and overlays the page, so the builder
     is pushed down by however much of it sits over the builder's top. */
  Builder.prototype.setOffset = function () {
    this.root.style.marginTop = '0px';
    var top = this.root.getBoundingClientRect().top + window.scrollY;
    var headerBottom = 0;
    $$(document, '.shopify-section-group-header-group, .shopify-section-group-header-group > *').forEach(function (el) {
      var pos = getComputedStyle(el).position;
      if (pos !== 'fixed' && pos !== 'sticky') return;
      var r = el.getBoundingClientRect();
      if (r.height > 0 && r.top < 200) headerBottom = Math.max(headerBottom, r.bottom + window.scrollY);
    });
    var push = Math.max(0, Math.round(headerBottom - top));
    this.root.style.marginTop = push + 'px';
    this.root.style.setProperty('--pk-gw-offset', Math.max(0, Math.round(top + push)) + 'px');
  };

  /* ---------- catalogue ---------- */

  Builder.prototype.fetchIndexPage = function (handle, page) {
    var url = this.rootUrl.replace(/\/$/, '') + '/collections/' + encodeURIComponent(handle) + '?section_id=pk-gw-index&page=' + page;
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var s = doc.querySelector('[data-pk-gw-index]');
        if (!s) return { list: [], pages: 1 };
        var list = [];
        try { list = JSON.parse(s.textContent).filter(Boolean); } catch (e) { list = []; }
        return { list: list, pages: parseInt(s.getAttribute('data-pages'), 10) || 1 };
      });
  };

  Builder.prototype.fetchCollection = function (handle) {
    var self = this;
    return this.fetchIndexPage(handle, 1).then(function (first) {
      var all = first.list.slice();
      if (first.pages <= 1) return all;
      var jobs = [];
      for (var p = 2; p <= first.pages; p++) jobs.push(self.fetchIndexPage(handle, p));
      return Promise.all(jobs).then(function (rest) {
        rest.forEach(function (r) { all = all.concat(r.list); });
        return all;
      });
    });
  };

  Builder.prototype.loadBest = function () {
    var self = this;
    return this.fetchCollection(this.best).then(function (list) { self.bestList = list; }).catch(function () {});
  };

  Builder.prototype.loadCatalog = function () {
    var self = this;
    return this.fetchCollection(this.catalog).then(function (list) {
      self.index = list;
      self.indexLoaded = true;
    }).catch(function () { self.indexLoaded = true; });
  };

  /* What the list shows:
     - searching: every print matching the words (plus any chips)
     - Best sellers on: best sellers, narrowed by any Mood/Colour/Shape chips
     - Best sellers off: all prints, narrowed by any chips
     Within a group chips are OR, across groups AND. */
  Builder.prototype.query = function () { return this.el.search.value.trim().toLowerCase(); };

  Builder.prototype.currentList = function () {
    if (this.giftMode) return this.giftList;
    var q = this.query();
    var tagKeys = Object.keys(this.activeTags);
    if (!q && this.bestOn && !tagKeys.length) return this.bestList.length ? this.bestList : this.index.slice(0, 60);
    var source;
    if (q || !this.bestOn) source = this.index.length ? this.index : this.bestList;
    else source = this.bestList;
    if (!q && !tagKeys.length) return source;
    var words = q ? q.split(/\s+/) : [];
    var groups = {};
    var self = this;
    tagKeys.forEach(function (tag) {
      var g = self.activeTags[tag];
      (groups[g] = groups[g] || []).push(tag);
    });
    return source.filter(function (p) {
      var tags = '|' + p.g + '|';
      var hay = (p.t + ' ' + p.v + ' ' + p.g).toLowerCase();
      for (var i = 0; i < words.length; i++) if (hay.indexOf(words[i]) === -1) return false;
      for (var g in groups) {
        var ok = groups[g].some(function (t) { return tags.indexOf('|' + t + '|') !== -1; });
        if (!ok) return false;
      }
      return true;
    });
  };

  Builder.prototype.renderList = function () {
    var self = this;
    var list = this.currentList();
    var q = this.query();
    var tagKeys = Object.keys(this.activeTags);
    var more = tagKeys.filter(function (t) { return self.activeTags[t] !== 'style'; }).length;
    this.el.filterCount.textContent = more ? '(' + more + ')' : '';
    if (!this.bestLabel) this.bestLabel = this.el.listTitle.textContent;
    var plural = function (n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); };
    var title;
    if (this.giftMode) title = plural(list.length, 'print') + ' to choose from';
    else if (q) title = plural(list.length, 'print');
    else if (this.bestOn && !tagKeys.length) title = this.bestLabel;
    else if (this.bestOn) title = plural(list.length, 'best seller');
    else if (!tagKeys.length) title = 'All prints';
    else title = plural(list.length, 'print');
    this.el.listTitle.textContent = title;
    var waitingForAll = !this.indexLoaded && (q || !this.bestOn) && !this.giftMode;
    var giftMode = this.giftMode;

    var limit = this._limit || 60;
    var html = '';
    var counts = this.wallCounts();
    list.slice(0, limit).forEach(function (p) {
      var price = giftMode ? 'Free' : 'From ' + money(p.p);
      html += '<button type="button" class="pk-gw__card" data-pk-gw-card="' + p.h + '">' +
        '<span class="pk-gw__card-img"><img loading="lazy" src="' + p.i + '" alt=""></span>' +
        '<span class="pk-gw__card-add">Add</span>' +
        (counts[p.h] ? '<span class="pk-gw__card-count">' + counts[p.h] + '</span>' : '') +
        '<span class="pk-gw__card-meta"><span class="pk-gw__card-title"></span><span class="pk-gw__card-artist"></span>' +
        '<span class="pk-gw__card-price">' + price + '</span></span></button>';
    });
    this.el.grid.innerHTML = html;
    /* titles/artists set as text so no product text is ever parsed as HTML */
    var cards = $$(this.el.grid, '[data-pk-gw-card]');
    list.slice(0, limit).forEach(function (p, i) {
      $(cards[i], '.pk-gw__card-title').textContent = p.t;
      $(cards[i], '.pk-gw__card-artist').textContent = p.v;
    });

    var status = '';
    if (giftMode && !this.giftList.length) status = 'Loading prints…';
    else if (waitingForAll || (!this.bestList.length && !this.indexLoaded && !giftMode)) status = 'Loading prints…';
    else if (!list.length) status = 'No prints match. Try fewer filters.';
    else if (list.length > limit) status = '';
    this.el.status.textContent = status;
    this._listLength = list.length;
  };

  Builder.prototype.wallCounts = function () {
    var c = {};
    this.items.forEach(function (it) { c[it.handle] = (c[it.handle] || 0) + 1; });
    return c;
  };

  /* ---------- rooms ---------- */

  Builder.prototype.renderRoomButtons = function () {
    var self = this;
    if (this.rooms.length < 2) { this.el.rooms.innerHTML = ''; return; }
    this.el.rooms.innerHTML = this.rooms.map(function (r, i) {
      return '<button type="button" class="pk-gw__room-btn" data-pk-gw-room-btn="' + i + '" aria-label="Room ' + (i + 1) + '"><img src="' + (r.thumb || r.src) + '" alt=""></button>';
    }).join('');
    $$(this.el.rooms, '[data-pk-gw-room-btn]').forEach(function (b) {
      b.addEventListener('click', function () { self.setRoom(parseInt(b.getAttribute('data-pk-gw-room-btn'), 10)); });
    });
  };

  Builder.prototype.setRoom = function (i) {
    this.roomIndex = i;
    var r = this.rooms[i];
    if (r.src) {
      this.el.room.src = r.src;
      this.el.room.hidden = false;
      this.el.fallback.hidden = true;
    } else {
      this.el.room.hidden = true;
      this.el.fallback.hidden = false;
    }
    $$(this.el.rooms, '[data-pk-gw-room-btn]').forEach(function (b) {
      b.classList.toggle('is-on', parseInt(b.getAttribute('data-pk-gw-room-btn'), 10) === i);
    });
    this.items.forEach(function (it) { it.manual = false; });
    this.fitCanvas();
    this.layout();
  };

  /* Shows the room photo's FULL height in the stage. On a stage narrower
     than the photo the sides are trimmed, keeping the wall centre in view;
     on a wider stage the photo is centred and the strips either side show a
     blurred copy of the room (CSS .pk-gw__stage::before). */
  Builder.prototype.fitCanvas = function () {
    var r = this.rooms[this.roomIndex];
    var sw = this.el.stage.clientWidth, sh = this.el.stage.clientHeight;
    if (!sw || !sh) return;
    var ch = sh, cw = sh * (r.w / r.h);
    var cx = (r.centerX || 50) / 100;
    var left = cw <= sw ? (sw - cw) / 2 : Math.min(0, Math.max(sw - cw, sw / 2 - cw * cx));
    var c = this.el.canvas.style;
    c.width = cw + 'px'; c.height = ch + 'px'; c.left = left + 'px'; c.top = '0px';
    this.canvasW = cw; this.canvasH = ch;
    this.el.stage.style.setProperty('--pk-gw-room-bg', r.src ? 'url("' + withWidth(r.src, 600) + '")' : 'none');
    this.renderItems();
  };

  /* inches -> fraction of canvas width */
  Builder.prototype.inchToFrac = function (inches) {
    var r = this.rooms[this.roomIndex];
    return (inches * 2.54 / (r.wallCm || 420)) * this.scale;
  };

  /* ---------- products on the wall ---------- */

  Builder.prototype.loadProduct = function (handle) {
    var self = this;
    if (this.products[handle]) return Promise.resolve(this.products[handle]);
    return fetch(this.rootUrl.replace(/\/$/, '') + '/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { if (!r.ok) throw new Error('product'); return r.json(); })
      .then(function (p) {
        var names = (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
        p._sizeIdx = names.indexOf('Size');
        p._frameIdx = names.indexOf('Frame');
        var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
        p._landscape = tags.indexOf('landscape') !== -1;
        self.products[handle] = p;
        return p;
      });
  };

  Builder.prototype.sizeOf = function (p, v) { return p._sizeIdx === -1 ? '' : v.options[p._sizeIdx]; };
  Builder.prototype.frameOf = function (p, v) { return p._frameIdx === -1 ? 'Unframed' : v.options[p._frameIdx]; };

  Builder.prototype.shapeKey = function (p) {
    var v = p.variants[0];
    var s = parseInches(this.sizeOf(p, v)) || { w: 3, h: 4 };
    if (Math.abs(s.w - s.h) < 0.01) return 'square';
    var isA = /\(a\d\)/i.test(this.sizeOf(p, v)) || Math.abs(s.h / s.w - 1.414) < 0.03;
    return (p._landscape ? 'landscape-' : 'portrait-') + (isA ? 'a' : '34');
  };

  Builder.prototype.frameImageFor = function (p, frameName) {
    var self = this;
    var want = String(frameName).toLowerCase();
    var v = p.variants.filter(function (x) { return String(self.frameOf(p, x)).toLowerCase() === want && x.featured_image; })[0];
    if (v) return v.featured_image.src;
    /* fall back to an image named after the frame colour */
    var imgs = p.images || [];
    for (var i = 0; i < imgs.length; i++) if (String(imgs[i]).toLowerCase().indexOf('_' + want) !== -1) return imgs[i];
    return p.featured_image || imgs[0];
  };

  Builder.prototype.unframedImageFor = function (p) {
    var imgs = p.images || [];
    for (var i = 0; i < imgs.length; i++) {
      var name = String(imgs[i]).split('?')[0].split('/').pop().toLowerCase();
      if (/(^|[_-])nf[._-]|nf\.(jpe?g|png|webp)$/.test(name)) return imgs[i];
    }
    return imgs[1] || null;
  };

  /* Loads and measures the unframed mockup once per product. */
  Builder.prototype.detectNFFor = function (p) {
    var self = this;
    if (this.nf[p.handle] !== undefined) return Promise.resolve(this.nf[p.handle]);
    var src = this.unframedImageFor(p);
    if (!src) { this.nf[p.handle] = false; return Promise.resolve(false); }
    return loadImage(withWidth(sameOrigin(src), 1200)).then(function (img) {
      var d = null;
      try { d = detectNF(img); } catch (e) { d = null; }
      self.nf[p.handle] = d ? { box: d.box, aspect: d.aspect, src: src } : false;
      return self.nf[p.handle];
    }).catch(function () { self.nf[p.handle] = false; return false; });
  };

  /* The unframed mockup is only used when its shape matches the size being
     sold (some products have e.g. a landscape mockup on a square print);
     otherwise the framed image is used. */
  Builder.prototype.nfFor = function (it) {
    var p = this.products[it.handle];
    if (!isUnframed(this.frameOf(p, it.variant))) return null;
    var nf = this.nf[it.handle];
    if (!nf) return null;
    var inch = this.itemInches(it);
    var want = inch.h / inch.w;
    if (Math.abs(nf.aspect - want) / want > 0.08) return null;
    return nf;
  };

  Builder.prototype.detectFor = function (p) {
    var self = this;
    if (this.boxes[p.handle]) return Promise.resolve(this.boxes[p.handle]);
    var src = withWidth(sameOrigin(this.frameImageFor(p, 'black')), IMG_W);
    var fallback = function () {
      var b = FALLBACK_BOXES[self.shapeKey(p)];
      return { box: b, frame: 26, w: IMG_W, h: IMG_H, fallback: true };
    };
    return loadImage(src).then(function (img) {
      var d = null;
      try { d = detectBox(img); } catch (e) { d = null; }
      return d || fallback();
    }).catch(fallback).then(function (d) { self.boxes[p.handle] = d; return d; });
  };

  Builder.prototype.pickDefaultVariant = function (p) {
    var self = this;
    var avail = p.variants.filter(function (v) { return v.available; });
    if (!avail.length) return null;
    var sizes = [];
    avail.forEach(function (v) { var s = self.sizeOf(p, v); if (sizes.indexOf(s) === -1) sizes.push(s); });
    var size = null;
    for (var i = 0; i < this.defaultSizes.length && !size; i++) {
      var want = this.defaultSizes[i];
      size = sizes.filter(function (s) { return s.toLowerCase().replace(/\s/g, '').indexOf(want.replace(/\s/g, '')) !== -1; })[0] || null;
    }
    if (!size) size = sizes[Math.min(2, sizes.length - 1)];
    var inSize = avail.filter(function (v) { return self.sizeOf(p, v) === size; });
    return inSize.filter(function (v) { return String(self.frameOf(p, v)).toLowerCase() === self.defaultFrame; })[0] ||
      inSize.filter(function (v) { return !isUnframed(self.frameOf(p, v)); })[0] || inSize[0];
  };

  Builder.prototype.addPrint = function (handle, card) {
    var self = this;
    if (card) card.classList.add('is-loading');
    var gift = this.giftMode;
    return this.loadProduct(handle).then(function (p) {
      var v = gift ? self.pickGiftVariant(p, gift) : self.pickDefaultVariant(p);
      if (!v) throw new Error(gift ? 'That print is not available as a free print. Please pick another.' : 'This print is sold out.');
      var needNF = gift ? self.detectNFFor(p) : Promise.resolve();
      return Promise.all([self.detectFor(p), needNF]).then(function () {
        var it = { id: self.nextId++, handle: handle, variant: v, manual: false, x: 0.5, y: 0.35, gift: gift || null, inBasket: false };
        self.items.push(it);
        if (gift) self.setGiftMode(null);
        self.layout();
        self.select(null);
        self.updateTotals();
        self.renderList();
      });
    }).catch(function (err) {
      self.toast(err && err.message && err.message !== 'product' ? err.message : 'Could not load that print. Please try again.');
    }).then(function () { if (card) card.classList.remove('is-loading'); });
  };

  /* ---------- offer ---------- */

  Builder.prototype.giftSizeOk = function (kind, size) {
    return this.offer.sizes[kind].indexOf(String(size).trim()) !== -1;
  };

  /* A free print starts unframed at the biggest size the offer allows. */
  Builder.prototype.pickGiftVariant = function (p, kind) {
    var self = this;
    var order = this.offer.sizes[kind];
    var ok = p.variants.filter(function (v) {
      return v.available && isUnframed(self.frameOf(p, v)) && self.giftSizeOk(kind, self.sizeOf(p, v));
    });
    ok.sort(function (a, b) { return order.indexOf(self.sizeOf(p, a).trim()) - order.indexOf(self.sizeOf(p, b).trim()); });
    return ok[0] || null;
  };

  Builder.prototype.fullPrice = function (it) { return it.variant.price; };
  /* Fixed amounts (pence) the "Poster Kings Free Prints" discount app takes
     off each gift line, by the size code in _pk_gift. Override with a
     data-gift-off JSON attribute on the section root if the app changes. */
  var GIFT_OFF = {
    free: { '6x8': 1300, 'A4': 1600, '12x16': 2000, 'A3': 2000 },
    large: { '18x24': 2999, 'A2': 2999 }
  };
  Builder.prototype.giftOff = function (it) {
    if (!this._giftOff) {
      this._giftOff = GIFT_OFF;
      try { var o = JSON.parse(this.root.getAttribute('data-gift-off') || 'null'); if (o) this._giftOff = o; } catch (e) {}
    }
    var code = giftSizeCode(this.sizeOf(this.products[it.handle], it.variant));
    var table = this._giftOff[it.gift === 'large' ? 'large' : 'free'] || {};
    return table[code] || 0;
  };
  Builder.prototype.giftCharge = function (it) {
    return Math.max(0, it.variant.price - this.giftOff(it));
  };

  /* Reads the basket the same way pk-cart-offer.liquid does. */
  Builder.prototype.loadBasket = function () {
    var self = this;
    return fetch(this.rootUrl.replace(/\/$/, '') + '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var b = { paid: 0, spend: 0, freeLive: false, largeLive: false, prints: 0 };
        (cart.items || []).forEach(function (line) {
          var tag = String((line.properties && line.properties._pk_gift) || '').trim();
          var disc = (line.line_level_discount_allocations || []).reduce(function (a, d) { return a + (d.amount || 0); }, 0);
          var isFree = tag.indexOf('free-') === 0, isLarge = tag.indexOf('large-') === 0;
          var live = disc > 0 && (isFree || isLarge);
          if (live && isFree) b.freeLive = true;
          if (live && isLarge) b.largeLive = true;
          if (!live) b.paid += line.quantity;
          b.prints += line.quantity;
          if (!isLarge) {
            var real = (line.original_price || line.price || 0) * line.quantity - disc;
            if (real > 0) b.spend += real;
          }
        });
        self.basket = b;
        self.updateTotals();
      }).catch(function () {});
  };

  /* Works out the three steps for basket + wall, and which wall gifts are
     live. Gifts are judged without counting themselves (as the cart does). */
  Builder.prototype.offerState = function () {
    var self = this, o = this.offer, b = this.basket;
    var fresh = this.items.filter(function (it) { return !it.inBasket; });
    var freeIt = fresh.filter(function (it) { return it.gift === 'free'; })[0] || null;
    var largeIt = fresh.filter(function (it) { return it.gift === 'large'; })[0] || null;
    var paidBase = b.paid, spendBase = b.spend;
    fresh.forEach(function (it) {
      if (it === freeIt || it === largeIt) return;
      paidBase += 1;
      spendBase += it.variant.price;
    });
    var freeActive = !!freeIt && !b.freeLive && paidBase >= o.giftEvery;
    var spendEx = spendBase + (freeIt ? (freeActive ? self.giftCharge(freeIt) : freeIt.variant.price) : 0);
    var freeClaimed = b.freeLive || freeActive;
    var largeActive = !!largeIt && !b.largeLive && freeClaimed && spendEx >= o.bonusMinCents;
    var paidAll = paidBase + (freeIt && !freeActive ? 1 : 0) + (largeIt && !largeActive ? 1 : 0);
    var spendAll = spendEx + (largeIt && !largeActive ? largeIt.variant.price : 0);
    var shipDone = o.shipMode === 'prints' ? paidAll >= o.shipMin : (o.shipAmountCents > 0 && spendAll >= o.shipAmountCents);
    var largeClaimed = b.largeLive || largeActive;
    return {
      freeIt: freeIt, largeIt: largeIt, freeActive: freeActive, largeActive: largeActive,
      paidBase: paidBase, paidAll: paidAll, spendEx: spendEx, spendAll: spendAll,
      shipDone: shipDone, freeClaimed: freeClaimed, largeClaimed: largeClaimed
    };
  };

  Builder.prototype.isGiftLive = function (it, st) {
    if (!it.gift) return false;
    if (it.inBasket) return true;
    return it.gift === 'free' ? (st.freeActive && it === st.freeIt) : (st.largeActive && it === st.largeIt);
  };

  Builder.prototype.renderRoad = function (st) {
    var o = this.offer, b = this.basket, el = this.el;
    if (!el.road) return;
    var plural = function (n, word) { return n + ' more ' + word + (n === 1 ? '' : 's'); };
    var msg = '', claim = null, focus = 0;

    if (!st.shipDone) {
      focus = 1;
      if (o.shipMode === 'prints') msg = 'Add ' + plural(o.shipMin - st.paidAll, 'print') + ' for free shipping';
      else msg = 'Spend ' + money(o.shipAmountCents - st.spendAll) + ' more for free shipping';
    } else if (!st.freeClaimed) {
      focus = 2;
      if (st.freeIt) msg = 'Add ' + plural(o.giftEvery - st.paidBase, 'print') + ' to make your free print free';
      else if (st.paidBase >= o.giftEvery) { msg = 'You have unlocked a free print'; claim = 'free'; }
      else msg = 'Add ' + plural(o.giftEvery - st.paidBase, 'print') + ' for a free print';
    } else if (!st.largeClaimed) {
      focus = 3;
      var left = o.bonusMinCents - st.spendEx;
      if (st.largeIt) msg = 'Spend ' + money(Math.max(0, left)) + ' more to make your Large print free';
      else if (left <= 0) { msg = 'You have unlocked a free Large print'; claim = 'large'; }
      else msg = 'Spend ' + money(left) + ' more for a free Large print';
    } else {
      msg = 'Every offer unlocked, enjoy your free prints';
    }
    if (st.shipDone && focus !== 1 && msg.indexOf('unlocked') === -1 && focus) msg = 'Free shipping unlocked. ' + msg;
    el.roadMsg.textContent = msg;
    el.roadNote.textContent = b.prints ? '(includes ' + b.prints + ' in your basket)' : '';

    el.roadClaim.hidden = !claim;
    el.roadClaim.setAttribute('data-kind', claim || '');
    el.roadClaim.textContent = claim === 'large' ? 'Claim free Large print' : 'Claim free print';
    if (this.giftMode && claim !== this.giftMode) this.setGiftMode(null);

    var states = [st.shipDone, st.freeClaimed, st.largeClaimed];
    $$(el.road, '[data-pk-gw-step]').forEach(function (li, i) {
      li.setAttribute('data-state', states[i] ? 'done' : (focus === i + 1 ? 'active' : 'waiting'));
    });
    var seg1 = st.freeClaimed ? 1 : Math.min(1, st.paidBase / o.giftEvery);
    var seg2 = st.largeClaimed ? 1 : (st.freeClaimed ? Math.min(1, st.spendEx / o.bonusMinCents) : 0);
    var s1 = $(el.road, '[data-pk-gw-seg="1"]'), s2 = $(el.road, '[data-pk-gw-seg="2"]');
    if (s1) s1.style.width = Math.round(seg1 * 100) + '%';
    if (s2) s2.style.width = Math.round(seg2 * 100) + '%';
  };

  Builder.prototype.setGiftMode = function (kind) {
    this.giftMode = kind;
    if (this.el.panel) this.el.panel.classList.toggle('is-gift-mode', !!kind);
    if (this.el.giftbar) {
      this.el.giftbar.hidden = !kind;
      this.el.giftbarText.textContent = kind === 'large' ? 'Choose your free Large print (A2 or 18x24")' : 'Choose your free print (up to A3)';
    }
    this._limit = 60;
    this.renderList();
    if (this.el.panel) this.el.panel.scrollTop = 0;
  };

  /* Physical size of an item in inches, including the frame moulding. */
  Builder.prototype.itemInches = function (it) {
    var p = this.products[it.handle];
    var s = parseInches(this.sizeOf(p, it.variant)) || { w: 16.5, h: 23.4 };
    var w = s.w, h = s.h;
    if (p._landscape) { var t = w; w = h; h = t; }
    if (!isUnframed(this.frameOf(p, it.variant))) { w += this.frameMarginIn * 2; h += this.frameMarginIn * 2; }
    return { w: w, h: h };
  };

  /* Width/height as fractions of the canvas. Height follows the image box
     so the picture is never stretched. */
  Builder.prototype.itemSize = function (it) {
    var d = this.boxes[it.handle];
    var inch = this.itemInches(it);
    var wf = this.inchToFrac(inch.w);
    var nf = this.nfFor(it);
    if (nf) return { w: wf, h: wf * this.canvasW * nf.aspect / this.canvasH };
    var box = d.box, unframed = isUnframed(this.frameOf(this.products[it.handle], it.variant));
    var bw = box[2] - box[0], bh = box[3] - box[1];
    if (unframed) { bw -= d.frame * 2; bh -= d.frame * 2; }
    var hf = wf * this.canvasW * (bh / bw) / this.canvasH;
    return { w: wf, h: hf };
  };

  /* Auto layout: prints that have not been dragged are arranged in centred
     rows around the room's wall centre. */
  Builder.prototype.layout = function () {
    var self = this;
    if (!this.canvasW) return;
    var r = this.rooms[this.roomIndex];
    var auto = this.items.filter(function (it) { return !it.manual; });
    if (!auto.length) { this.renderItems(); return; }
    var gapX = this.inchToFrac(3);
    var gapY = gapX * this.canvasW / this.canvasH;
    var maxRow = 0.6;
    var rows = [], row = [], rowW = 0;
    auto.forEach(function (it) {
      var s = self.itemSize(it);
      if (row.length && rowW + gapX + s.w > maxRow) { rows.push({ items: row, w: rowW }); row = []; rowW = 0; }
      rowW += (row.length ? gapX : 0) + s.w;
      row.push({ it: it, s: s });
    });
    if (row.length) rows.push({ items: row, w: rowW });
    rows.forEach(function (rw) { rw.h = Math.max.apply(null, rw.items.map(function (e) { return e.s.h; })); });
    var totalH = rows.reduce(function (a, rw) { return a + rw.h; }, 0) + gapY * (rows.length - 1);
    var y = (r.hangY || 34) / 100 - totalH / 2;
    var cx = (r.centerX || 50) / 100;
    rows.forEach(function (rw) {
      var x = cx - rw.w / 2;
      rw.items.forEach(function (e) {
        e.it.x = x + e.s.w / 2;
        e.it.y = y + rw.h / 2;
        x += e.s.w + gapX;
      });
      y += rw.h + gapY;
    });
    this.renderItems();
  };

  Builder.prototype.renderItems = function () {
    var self = this;
    if (!this.canvasW) return;
    var existing = {};
    $$(this.el.items, '[data-pk-gw-item]').forEach(function (n) { existing[n.getAttribute('data-pk-gw-item')] = n; });

    this.items.forEach(function (it) {
      var n = existing[it.id];
      delete existing[it.id];
      if (!n) {
        n = document.createElement('div');
        n.className = 'pk-gw__item';
        n.setAttribute('data-pk-gw-item', it.id);
        n.setAttribute('role', 'button');
        n.setAttribute('tabindex', '0');
        self.el.items.appendChild(n);
        self.bindItem(n, it);
      }
      var p = self.products[it.handle];
      var d = self.boxes[it.handle];
      var frame = self.frameOf(p, it.variant);
      var unframed = isUnframed(frame);
      var s = self.itemSize(it);
      var pxW = s.w * self.canvasW;

      var st = n.style;
      var nf = self.nfFor(it);
      var src, box;
      if (nf) {
        src = withWidth(sameOrigin(nf.src), 2362);
        box = nf.box;
      } else {
        src = withWidth(sameOrigin(unframed ? self.frameImageFor(p, 'black') : self.frameImageFor(p, frame)), IMG_W);
        var bx = d.box.slice();
        var inset = unframed ? d.frame : 1;
        bx[0] += inset; bx[1] += inset; bx[2] -= inset; bx[3] -= inset;
        box = [bx[0] / d.w, bx[1] / d.h, bx[2] / d.w, bx[3] / d.h];
      }
      /* whole-pixel size and position keep the canvas sharp */
      var pw = Math.round(s.w * self.canvasW), ph = Math.round(s.h * self.canvasH);
      self.paintItem(n, src, box, pw, ph);
      st.width = pw + 'px';
      st.height = ph + 'px';
      st.left = Math.round(it.x * self.canvasW - pw / 2) + 'px';
      st.top = Math.round(it.y * self.canvasH - ph / 2) + 'px';
      /* shadow grows with the print; paper sits flat so its shadow is tighter */
      var blur, off;
      if (unframed) {
        blur = Math.max(2, pxW * 0.025); off = Math.max(1, pxW * 0.008);
        st.setProperty('--pk-gw-shadow', '0 ' + off + 'px ' + blur + 'px rgba(0,0,0,.16), 0 0 1px rgba(0,0,0,.18)');
      } else {
        blur = Math.max(4, pxW * 0.06); off = Math.max(2, pxW * 0.025);
        st.setProperty('--pk-gw-shadow', off * 0.4 + 'px ' + off + 'px ' + blur + 'px rgba(0,0,0,.28), 0 0 ' + Math.max(1, pxW * 0.01) + 'px rgba(0,0,0,.12)');
      }
      n.classList.toggle('is-unframed', unframed);
      n.classList.toggle('is-selected', it.id === self.selectedId);
      n.setAttribute('aria-label', p.title + ', ' + self.sizeOf(p, it.variant) + ', ' + frame);
    });
    Object.keys(existing).forEach(function (k) { existing[k].remove(); });
    this.el.empty.hidden = this.items.length > 0;
    this.positionPop();
  };

  /* Paints one print at its on-screen size x screen density. Skips work
     when nothing changed; waits for the size transition before repainting a
     resized print so it is only drawn once, at its final size. */
  Builder.prototype.paintItem = function (n, src, box, cssW, cssH) {
    var dpr = Math.min(3, window.devicePixelRatio || 1);
    var w = Math.max(1, Math.round(cssW * dpr)), h = Math.max(1, Math.round(cssH * dpr));
    var key = src + '|' + box.join(',') + '|' + w + 'x' + h;
    if (n._pkKey === key) return;
    n._pkKey = key;
    var cv = n.querySelector('canvas');
    if (!cv) { cv = document.createElement('canvas'); n.appendChild(cv); }
    var sameSrc = n._pkSrc === src + '|' + box.join(',');
    if (!sameSrc) n.classList.add('is-loading');
    clearTimeout(n._pkT);
    n._pkT = setTimeout(function () {
      getImage(src).then(function (img) {
        if (n._pkKey !== key) return;
        paintCrop(cv, img, box, w, h);
        n._pkSrc = src + '|' + box.join(',');
        n.classList.remove('is-loading');
      }).catch(function () { n.classList.remove('is-loading'); });
    }, sameSrc ? 380 : 0);
  };

  Builder.prototype.bindItem = function (n, it) {
    var self = this;
    var start = null;
    n.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      e.preventDefault();
      n.setPointerCapture(e.pointerId);
      start = { x: e.clientX, y: e.clientY, ix: it.x, iy: it.y, moved: false };
    });
    n.addEventListener('pointermove', function (e) {
      if (!start) return;
      var dx = e.clientX - start.x, dy = e.clientY - start.y;
      if (!start.moved && Math.abs(dx) + Math.abs(dy) < 5) return;
      if (!start.moved) { start.moved = true; n.classList.add('is-dragging'); self.select(null); }
      it.manual = true;
      it.x = start.ix + dx / self.canvasW;
      it.y = start.iy + dy / self.canvasH;
      n.style.left = Math.round(it.x * self.canvasW - n.offsetWidth / 2) + 'px';
      n.style.top = Math.round(it.y * self.canvasH - n.offsetHeight / 2) + 'px';
    });
    function end() {
      if (!start) return;
      var wasClick = !start.moved;
      start = null;
      n.classList.remove('is-dragging');
      if (wasClick) self.select(self.selectedId === it.id ? null : it.id);
    }
    n.addEventListener('pointerup', end);
    n.addEventListener('pointercancel', end);
    n.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.select(it.id); }
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); self.remove(it.id); }
    });
  };

  /* ---------- popover ---------- */

  Builder.prototype.select = function (id) {
    this.selectedId = id;
    $$(this.el.items, '[data-pk-gw-item]').forEach(function (n) {
      n.classList.toggle('is-selected', String(id) === n.getAttribute('data-pk-gw-item'));
    });
    if (!id) { this.el.pop.hidden = true; return; }
    this.renderPop();
    this.el.pop.hidden = false;
    this.positionPop();
  };

  Builder.prototype.itemById = function (id) { return this.items.filter(function (i) { return i.id === id; })[0]; };

  Builder.prototype.renderPop = function () {
    var self = this;
    var it = this.itemById(this.selectedId);
    if (!it) return;
    var p = this.products[it.handle];
    var curSize = this.sizeOf(p, it.variant);
    var curFrame = this.frameOf(p, it.variant);

    this.el.popTitle.textContent = (it.gift === 'free' ? 'Free print: ' : it.gift === 'large' ? 'Free Large print: ' : '') + p.title;

    var sizes = [];
    p.variants.forEach(function (v) {
      var s = self.sizeOf(p, v);
      if (!v.available || sizes.indexOf(s) !== -1) return;
      if (it.gift && !self.giftSizeOk(it.gift, s)) return;
      sizes.push(s);
    });
    this.el.popSize.innerHTML = '';
    sizes.forEach(function (s) {
      var o = document.createElement('option');
      o.value = s; o.textContent = s;
      if (s === curSize) o.selected = true;
      self.el.popSize.appendChild(o);
    });

    var frames = [];
    p.variants.forEach(function (v) { var f = self.frameOf(p, v); if (frames.indexOf(f) === -1) frames.push(f); });
    this.el.popFrames.innerHTML = '';
    frames.forEach(function (f) {
      var ok = p.variants.some(function (v) { return v.available && self.sizeOf(p, v) === curSize && self.frameOf(p, v) === f; });
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pk-gw__swatch' + (f === curFrame ? ' is-on' : '');
      b.disabled = !ok;
      var i = document.createElement('i');
      i.style.background = FRAME_SWATCHES[String(f).toLowerCase()] || '#999';
      b.appendChild(i);
      b.appendChild(document.createTextNode(f));
      b.addEventListener('click', function () { self.changeVariant(it, curSize, f); });
      self.el.popFrames.appendChild(b);
    });

    this.renderPopPrice();
  };

  Builder.prototype.renderPopPrice = function () {
    var it = this.itemById(this.selectedId);
    if (!it) return;
    if (this.isGiftLive(it, this.offerState())) {
      var c = this.giftCharge(it);
      this.el.popPrice.textContent = c ? money(c) + ' after ' + money(this.giftOff(it)) + ' off (free print)' : 'Free';
    } else {
      this.el.popPrice.textContent = money(it.variant.price);
    }
  };

  Builder.prototype.changeVariant = function (it, size, frame) {
    var self = this;
    var p = this.products[it.handle];
    var match = function (v, s, f) { return v.available && self.sizeOf(p, v) === s && (f === null || self.frameOf(p, v) === f); };
    /* keep any other option (e.g. Print Material) the same where possible */
    var others = function (v) {
      return v.options.every(function (o, i) { return i === p._sizeIdx || i === p._frameIdx || o === it.variant.options[i]; });
    };
    var list = p.variants.filter(function (v) { return match(v, size, frame); });
    var v = list.filter(others)[0] || list[0];
    if (!v) {
      /* size not offered in this frame (e.g. A0 is unframed only) */
      list = p.variants.filter(function (x) { return match(x, size, null); });
      v = list.filter(function (x) { return isUnframed(self.frameOf(p, x)); })[0] || list[0];
      if (v) this.toast(size + ' comes ' + self.frameOf(p, v).toLowerCase() + ' only');
    }
    if (!v) return;
    var apply = function () {
      if (it.variant !== v) it.inBasket = false;
      it.variant = v;
      if (!it.manual) self.layout(); else self.renderItems();
      if (self.selectedId === it.id) self.renderPop();
      self.updateTotals();
    };
    if (isUnframed(this.frameOf(p, v)) && this.nf[it.handle] === undefined) {
      var node = $(this.el.items, '[data-pk-gw-item="' + it.id + '"]');
      if (node) node.classList.add('is-loading');
      this.detectNFFor(p).then(function () {
        if (node) node.classList.remove('is-loading');
        apply();
      });
      return;
    }
    apply();
  };

  Builder.prototype.positionPop = function () {
    if (this.el.pop.hidden || !this.selectedId) return;
    var n = $(this.el.items, '[data-pk-gw-item="' + this.selectedId + '"]');
    if (!n) return;
    var stageR = this.el.stage.getBoundingClientRect();
    var r = n.getBoundingClientRect();
    var pw = this.el.pop.offsetWidth || 250, ph = this.el.pop.offsetHeight || 200;
    var left = r.right - stageR.left + 14;
    if (left + pw > stageR.width - 10) left = r.left - stageR.left - pw - 14;
    if (left < 10) left = Math.min(stageR.width - pw - 10, Math.max(10, r.left - stageR.left + r.width / 2 - pw / 2));
    var top = r.top - stageR.top + r.height / 2 - ph / 2;
    top = Math.max(10, Math.min(stageR.height - ph - 10, top));
    this.el.pop.style.left = left + 'px';
    this.el.pop.style.top = top + 'px';
  };

  Builder.prototype.remove = function (id) {
    this.items = this.items.filter(function (i) { return i.id !== id; });
    this.select(null);
    this.layout();
    this.updateTotals();
    this.renderList();
  };

  /* ---------- totals & cart ---------- */

  /* Subtotal = what the wall will cost with the offer applied: a live gift
     costs its price minus the app's fixed amount. The struck-through price is
     the full price. */
  Builder.prototype.updateTotals = function () {
    var self = this;
    var st = this.offerState();
    var total = 0, compare = 0;
    this.items.forEach(function (it) {
      var live = self.isGiftLive(it, st);
      total += live ? self.giftCharge(it) : it.variant.price;
      compare += Math.max(it.variant.compare_at_price || 0, it.variant.price);
    });
    this.el.subtotal.textContent = money(total);
    if (compare > total) { this.el.compare.textContent = money(compare); this.el.compare.hidden = false; }
    else this.el.compare.hidden = true;
    var pending = this.items.some(function (it) { return !it.inBasket; });
    this.el.add.disabled = !pending;
    if (!this.el.add.classList.contains('is-busy')) {
      this.el.add.textContent = (this.items.length && !pending) ? 'Wall in basket' : this.addLabel;
    }
    this.renderRoad(st);
    if (this.selectedId && !this.el.pop.hidden) this.renderPopPrice();
  };

  /* Adds everything not yet in the basket. Paid prints of the same variant
     are merged; a live gift goes on its own line with the cart's _pk_gift
     tag so the discount app makes it free. */
  Builder.prototype.addToCart = function () {
    var self = this;
    var pending = this.items.filter(function (it) { return !it.inBasket; });
    if (!pending.length || this.el.add.classList.contains('is-busy')) return;
    var st = this.offerState();
    var qty = {}, order = [], items = [];
    pending.forEach(function (it) {
      if (self.isGiftLive(it, st)) {
        var code = giftSizeCode(self.sizeOf(self.products[it.handle], it.variant));
        items.push({ id: it.variant.id, quantity: 1, properties: { _pk_gift: (it.gift === 'free' ? 'free-' : 'large-') + code } });
        return;
      }
      var id = it.variant.id;
      if (!qty[id]) { qty[id] = 0; order.push(id); }
      qty[id] += 1;
    });
    items = order.map(function (id) { return { id: id, quantity: qty[id] }; }).concat(items);
    this.el.add.classList.add('is-busy');
    this.el.add.textContent = 'Adding…';
    fetch(this.rootUrl.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (j) { if (!r.ok) throw j; return j; });
    }).then(function () {
      pending.forEach(function (it) { it.inBasket = true; });
      self.el.add.textContent = 'Added';
      var refresh = (window.pkCart && typeof window.pkCart.refresh === 'function') ? window.pkCart.refresh() : null;
      if (!refresh) { window.location.href = self.cartUrl; return; }
      return Promise.resolve(refresh).then(function () { return self.loadBasket(); });
    }).catch(function (err) {
      self.toast((err && (err.description || err.message)) || 'Could not add to basket. Please try again.');
    }).then(function () {
      setTimeout(function () { self.el.add.classList.remove('is-busy'); self.updateTotals(); }, 900);
    });
  };

  Builder.prototype.toast = function (msg) {
    var t = this.el.toast;
    t.textContent = msg;
    t.hidden = false;
    clearTimeout(this._toastT);
    this._toastT = setTimeout(function () { t.hidden = true; }, 2600);
  };

  /* ---------- events ---------- */

  Builder.prototype.bind = function () {
    var self = this;

    this.el.grid.addEventListener('click', function (e) {
      var card = e.target.closest('[data-pk-gw-card]');
      if (!card || card.classList.contains('is-loading')) return;
      self.addPrint(card.getAttribute('data-pk-gw-card'), card);
    });

    var searchT;
    this.el.search.addEventListener('input', function () {
      clearTimeout(searchT);
      searchT = setTimeout(function () { self._limit = 60; self.renderList(); self.el.grid.parentNode.scrollTop = 0; }, 150);
    });

    this.el.filtersToggle.addEventListener('click', function () {
      var open = self.el.filters.hidden;
      self.el.filters.hidden = !open;
      self.el.filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    var setChip = function (chip, on) {
      chip.classList.toggle('is-on', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    };
    var refresh = function () {
      self._limit = 60;
      self.renderList();
      self.el.grid.closest('.pk-gw__panel').scrollTop = 0;
    };
    var allChips = $$(this.el.panelTop, '[data-pk-gw-tag]');
    allChips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var tag = chip.getAttribute('data-pk-gw-tag');
        var group = chip.closest('[data-pk-gw-fgroup]').getAttribute('data-pk-gw-fgroup');
        if (self.activeTags[tag]) delete self.activeTags[tag]; else self.activeTags[tag] = group;
        setChip(chip, !!self.activeTags[tag]);
        /* choosing a style switches Best sellers off */
        if (group === 'style' && self.activeTags[tag] && self.bestOn) {
          self.bestOn = false;
          if (self.el.bestChip) setChip(self.el.bestChip, false);
        }
        refresh();
      });
    });
    if (this.el.bestChip) {
      this.el.bestChip.addEventListener('click', function () {
        self.bestOn = !self.bestOn;
        setChip(self.el.bestChip, self.bestOn);
        /* Best sellers on clears the other styles */
        if (self.bestOn) {
          allChips.forEach(function (c) {
            var t = c.getAttribute('data-pk-gw-tag');
            if (self.activeTags[t] === 'style') { delete self.activeTags[t]; setChip(c, false); }
          });
        }
        refresh();
      });
    }
    $(this.el.filters, '[data-pk-gw-clear]').addEventListener('click', function () {
      self.activeTags = {};
      allChips.forEach(function (c) { setChip(c, false); });
      self.bestOn = true;
      if (self.el.bestChip) setChip(self.el.bestChip, true);
      refresh();
    });

    /* infinite scroll in the panel */
    var panel = this.el.grid.closest('.pk-gw__panel');
    var onScroll = function () {
      var el = window.matchMedia('(max-width: 989px)').matches ? document.documentElement : panel;
      var bottom = window.matchMedia('(max-width: 989px)').matches
        ? window.innerHeight + window.scrollY >= document.body.scrollHeight - 600
        : el.scrollTop + el.clientHeight >= el.scrollHeight - 600;
      if (bottom && (self._listLength || 0) > (self._limit || 60)) {
        self._limit = (self._limit || 60) + 60;
        self.renderList();
      }
    };
    panel.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    this.el.scale.addEventListener('input', function () {
      self.scale = parseInt(self.el.scale.value, 10) / 100;
      self.layout();
    });

    this.el.popSize.addEventListener('change', function () {
      var it = self.itemById(self.selectedId);
      if (!it) return;
      self.changeVariant(it, self.el.popSize.value, self.frameOf(self.products[it.handle], it.variant));
    });
    this.el.popRemove.addEventListener('click', function () { if (self.selectedId) self.remove(self.selectedId); });

    this.el.stage.addEventListener('pointerdown', function (e) {
      if (!e.target.closest('[data-pk-gw-item]') && !e.target.closest('[data-pk-gw-pop]')) self.select(null);
    });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.select(null); });

    this.el.add.addEventListener('click', function () { self.addToCart(); });

    if (this.el.roadClaim) this.el.roadClaim.addEventListener('click', function () {
      var kind = self.el.roadClaim.getAttribute('data-kind');
      if (kind) self.setGiftMode(self.giftMode === kind ? null : kind);
    });
    if (this.el.giftbarCancel) this.el.giftbarCancel.addEventListener('click', function () { self.setGiftMode(null); });

    this.el.close.addEventListener('click', function (e) {
      if (document.referrer && document.referrer.indexOf(location.host) !== -1 && history.length > 1) {
        e.preventDefault();
        history.back();
      }
    });

    if (window.ResizeObserver) new ResizeObserver(function () { self.fitCanvas(); }).observe(this.el.stage);
  };

  function boot() {
    $$(document, '[data-pk-gw]').forEach(function (root) {
      if (root._pkgw) return;
      root._pkgw = new Builder(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
